/*
 * Dinowax cart drawer.
 *
 * HOW THIS COEXISTS WITH FLEX'S CART.
 * z__jsAjaxCart.js binds a delegated jQuery handler to '.ajax-submit' and that
 * is what adds to cart from every product form on the site. It is left running
 * exactly as it is. On a successful add it calls
 * Shopify.theme.jsAjaxCart.showDrawer(); this file replaces showDrawer and
 * hideDrawer with its own, so the same add surfaces here instead of in Flex's
 * drawer. Flex's [data-ajax-cart-close] handler calls hideDrawer() as well, so
 * its close targets keep working without knowing anything changed.
 *
 * That object is created by a script tag inside the ajax-cart snippet, which
 * may execute after this file, so the patch is retried briefly rather than
 * assumed — see adopt().
 *
 * CONTENTS COME FROM LIQUID, NOT FROM JS.
 * Every change refetches the section through the Section Rendering API and
 * swaps the drawer's innerHTML, so money formatting, discount allocations,
 * market pricing and the upsell query stay server-side where they are already
 * correct. Reimplementing Shopify's money rules in JavaScript is how drawers
 * end up disagreeing with checkout.
 */
(function () {
  'use strict';

  var SECTION = 'dw-cart-drawer';
  var ROOT_SELECTOR = '[data-dw-cart]';
  var OPEN_CLASS = 'dw-cart-open';

  var busy = false;
  var lastFocus = null;
  var refreshToken = 0;
  var live = null;

  function root() {
    return document.querySelector(ROOT_SELECTOR);
  }

  function reduced() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* The section publishes its configured duration, so motion here stays in
     step with the panel slide rather than hard-coding a second number. */
  function duration() {
    var el = root();
    var d = el && parseInt(el.getAttribute('data-dw-duration'), 10);
    return !d || isNaN(d) ? 380 : d;
  }

  /*
   * FLIP. The whole drawer is re-rendered on every change, so rows do not move
   * — they are destroyed and rebuilt somewhere else, which reads as a snap.
   * Measuring each row before the swap and again after lets the ones that
   * survived be put back where they were and then transitioned to their new
   * position, so removing an item slides the rest up instead of teleporting
   * them. Rows with no "before" are new, and fade in rather than appearing.
   */
  function flip(el, before) {
    var d = duration();
    var rows = el.querySelectorAll('[data-dw-cart-item]');
    Array.prototype.forEach.call(rows, function (row) {
      var key = row.getAttribute('data-key');
      var prev = before[key];
      if (prev) {
        var dy = prev - row.getBoundingClientRect().top;
        if (Math.abs(dy) < 1) return;
        row.style.transition = 'none';
        row.style.transform = 'translateY(' + dy + 'px)';
        requestAnimationFrame(function () {
          row.style.transition = 'transform ' + d + 'ms cubic-bezier(0.22, 1, 0.36, 1)';
          row.style.transform = '';
          setTimeout(function () {
            row.style.transition = '';
            row.style.transform = '';
          }, d + 60);
        });
      } else if (row.animate) {
        row.animate(
          [{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'none' }],
          { duration: d, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
        );
      }
    });
  }

  /*
   * Collapse a row before its removal is sent, so the gap closes smoothly
   * instead of the row vanishing the moment the server answers. Height is
   * animated from an explicit pixel value because auto does not interpolate,
   * and the padding has to come down with it or the row leaves a gap behind.
   */
  function collapseRow(row) {
    if (!row || reduced()) return Promise.resolve();
    var d = duration();
    return new Promise(function (resolve) {
      var cs = window.getComputedStyle(row);
      row.style.overflow = 'hidden';
      row.style.height = row.offsetHeight + 'px';
      row.style.paddingTop = cs.paddingTop;
      row.style.paddingBottom = cs.paddingBottom;
      void row.offsetHeight;
      row.style.transition =
        'height ' + d + 'ms cubic-bezier(0.4, 0, 0.2, 1),' +
        'opacity ' + Math.round(d * 0.6) + 'ms ease,' +
        'padding ' + d + 'ms cubic-bezier(0.4, 0, 0.2, 1)';
      row.style.height = '0px';
      row.style.opacity = '0';
      row.style.paddingTop = '0px';
      row.style.paddingBottom = '0px';
      setTimeout(resolve, d);
    });
  }

  function panelOf(el) {
    return el ? el.querySelector('[data-dw-cart-panel]') : null;
  }

  /*
   * A single announcer that lives on <body>, deliberately NOT inside the
   * drawer. Everything inside the drawer is destroyed on every refresh, and a
   * live region that is removed and re-added is not reliably announced — so an
   * aria-live placed with the quantity would go silent exactly when it mattered.
   */
  function announce(msg) {
    if (!msg) return;
    if (!live) {
      live = document.createElement('p');
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      live.style.cssText =
        'position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0';
      document.body.appendChild(live);
    }
    live.textContent = msg;
  }

  /* ---- open / close ------------------------------------------------------ */

  function open() {
    var el = root();
    if (!el) return;
    var panel = panelOf(el);

    // refresh() may have just replaced this panel. Without reading a layout
    // property first the browser can coalesce "inserted at translateX(100%)"
    // and "now translateX(0)" into a single style resolution, and the slide-in
    // never runs — the drawer just appears.
    if (panel) void panel.offsetWidth;

    lastFocus = document.activeElement;
    el.setAttribute('data-open', '');
    el.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add(OPEN_CLASS);
    document.body.classList.add(OPEN_CLASS);
    focusPanel(el);
  }

  function close() {
    var el = root();
    if (!el) return;
    el.removeAttribute('data-open');
    el.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove(OPEN_CLASS);
    document.body.classList.remove(OPEN_CLASS);
    if (lastFocus && lastFocus.focus && document.contains(lastFocus)) {
      try { lastFocus.focus({ preventScroll: true }); } catch (e) { /* gone */ }
    }
    lastFocus = null;
  }

  function isOpen() {
    var el = root();
    return !!(el && el.hasAttribute('data-open'));
  }

  /*
   * The scrim also carries [data-dw-cart-close] and comes first in the markup,
   * so querying that attribute alone lands focus on a non-focusable <div> and
   * the Tab trap never engages. Target the button itself.
   */
  function focusPanel(el) {
    var target = el.querySelector('.dw-cart__close') || panelOf(el);
    if (!target) return;
    if (target.getAttribute && !target.hasAttribute('tabindex') && target.tagName === 'ASIDE') {
      target.setAttribute('tabindex', '-1');
    }
    try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
  }

  /* ---- re-render from Liquid --------------------------------------------- */

  /* Identify the control that was activated so focus can be put back on its
     replacement after the subtree is destroyed. */
  function fingerprint(el) {
    if (!el || !el.getAttribute) return null;
    var key = el.getAttribute('data-key');
    if (el.hasAttribute('data-dw-cart-qty')) {
      return '[data-dw-cart-qty][data-key="' + key + '"][data-dir="' + el.getAttribute('data-dir') + '"]';
    }
    if (el.hasAttribute('data-dw-cart-remove')) return '[data-dw-cart-remove][data-key="' + key + '"]';
    if (el.hasAttribute('data-dw-cart-add')) return '[data-dw-cart-add][data-id="' + el.getAttribute('data-id') + '"]';
    return null;
  }

  function refresh(restoreSelector) {
    var url;
    try {
      url = new URL(window.location.href);
    } catch (e) {
      return Promise.resolve();
    }
    url.searchParams.set('section_id', SECTION);

    var token = ++refreshToken;

    return fetch(url.toString(), { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        // A slower earlier request must never overwrite a newer render.
        if (token !== refreshToken || !html) return;
        var el = root();
        if (!el) return;
        var box = document.createElement('div');
        box.innerHTML = html;
        var fresh = box.querySelector(ROOT_SELECTOR);
        if (!fresh) return;

        /* Capture what the shopper is currently looking at. Replacing the
           innerHTML destroys the scroll container, so without this every
           quantity change silently threw them back to the top of the cart. */
        var oldBody = el.querySelector('[data-dw-cart-body]');
        var scrollTop = oldBody ? oldBody.scrollTop : 0;
        var before = {};
        Array.prototype.forEach.call(el.querySelectorAll('[data-dw-cart-item]'), function (r) {
          before[r.getAttribute('data-key')] = r.getBoundingClientRect().top;
        });

        // Only the contents are replaced; the wrapper holds data-open, so the
        // panel is inserted already open and the slide does not replay.
        el.innerHTML = fresh.innerHTML;

        var newBody = el.querySelector('[data-dw-cart-body]');
        if (newBody && scrollTop) {
          // Restore instantly — scroll-behavior:smooth would animate the
          // restore itself, which looks like the drawer scrolling on its own.
          var prior = newBody.style.scrollBehavior;
          newBody.style.scrollBehavior = 'auto';
          newBody.scrollTop = scrollTop;
          newBody.style.scrollBehavior = prior;
        }

        if (!reduced()) flip(el, before);

        if (el.hasAttribute('data-open')) {
          var back = restoreSelector && el.querySelector(restoreSelector);
          if (back) {
            try { back.focus({ preventScroll: true }); } catch (e) { back.focus(); }
          } else if (restoreSelector) {
            focusPanel(el);
          }
        }
      })
      .catch(function () { /* leave the current contents up */ });
  }

  /* ---- cart mutations ---------------------------------------------------- */

  function setBusy(on) {
    busy = on;
    var el = root();
    if (!el) return;
    // Drives the subtotal's loading state; the figure is stale from the moment
    // a change is sent until the re-render replaces it.
    if (on) el.setAttribute('data-dw-busy', '');
    else el.removeAttribute('data-dw-busy');
    if (!on) {
      Array.prototype.forEach.call(el.querySelectorAll('[data-dw-loading]'), function (b) {
        b.removeAttribute('data-dw-loading');
      });
    }
    var btns = el.querySelectorAll('[data-dw-cart-qty],[data-dw-cart-remove],[data-dw-cart-add]');
    Array.prototype.forEach.call(btns, function (b) {
      // aria-disabled rather than the disabled property: disabling the button
      // that was just activated blurs it, and the re-entrancy guard below
      // already stops double submits.
      if (on) b.setAttribute('aria-disabled', 'true');
      else b.removeAttribute('aria-disabled');
    });
  }

  function syncCount(cart) {
    if (!cart || typeof cart.item_count !== 'number') return;
    var nodes = document.querySelectorAll('[data-bind="itemCount"]');
    Array.prototype.forEach.call(nodes, function (n) { n.textContent = cart.item_count; });
    // Flex hides the badge with .has-cart-count; without this an emptied cart
    // leaves a header badge reading 0.
    var triggers = document.querySelectorAll('[data-ajax-cart-trigger]');
    Array.prototype.forEach.call(triggers, function (t) {
      if (cart.item_count > 0) t.classList.add('has-cart-count');
      else t.classList.remove('has-cart-count');
    });
  }

  /*
   * The /cart page renders its remove and quantity controls keyed by LINE
   * INDEX, not by line key. A removal made from the drawer renumbers the
   * server's lines, so the page behind would then address the wrong one — its
   * remove button would delete a different product. Re-deriving those indexes
   * safely means re-rendering the page, so on the cart template a mutation
   * reloads rather than leaving a live trap behind the drawer.
   */
  function onCartPage() {
    return !!document.querySelector('.cart__item-list, .cart__form');
  }

  function post(path, body) {
    return fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      // Shopify answers a rejected add or change with 422 AND a JSON body, so
      // parsing without checking status reports failure as success and the
      // shopper sees nothing happen.
      return r.json().then(function (data) {
        if (!r.ok) {
          var err = new Error((data && data.description) || 'Cart error');
          err.cartError = data;
          throw err;
        }
        return data;
      });
    });
  }

  function showError(message) {
    var el = root();
    if (!el || !message) return;
    var slot = el.querySelector('[data-dw-cart-error]');
    if (slot) {
      slot.textContent = message;
      slot.hidden = false;
    }
    announce(message);
  }

  function mutate(makeRequest, restoreSelector, successMessage, pre) {
    // Resolves true on success, false if it was refused or failed. Callers that
    // predate this ignore the value; the cross-sell buy bar needs it to know
    // when to put its label back.
    if (busy) return Promise.resolve(false);
    setBusy(true);
    return (pre || Promise.resolve())
      .then(makeRequest)
      .then(function (cart) {
        syncCount(cart);
        if (onCartPage()) {
          window.location.reload();
          return null;
        }
        return refresh(restoreSelector).then(function () {
          announce(successMessage);
        });
      })
      .then(function () { setBusy(false); return true; })
      .catch(function (err) {
        setBusy(false);
        showError(err && err.message ? err.message : 'Something went wrong. Please try again.');
        return false;
      });
  }

  /*
   * Public multi-item add, used by the cross-sell block.
   *
   * Deliberately a thin wrapper. Everything that has to happen after an add —
   * the header count, the /cart page reload, the drawer re-render, the polite
   * announcement, the busy lock and the error surface — already lives in
   * mutate(). A cross-sell that posted to /cart/add.js on its own would have to
   * reimplement all of it, and that is exactly how a drawer drifts out of step
   * with the cart it is meant to be showing.
   */
  function addItems(items, restoreSelector) {
    if (!items || !items.length) return Promise.resolve(false);
    return mutate(
      function () {
        return post('/cart/add.js', { items: items }).then(function () {
          return fetch('/cart.js', { credentials: 'same-origin' })
            .then(function (r) { return r.json(); });
        });
      },
      restoreSelector || null,
      items.length > 1 ? 'Items added to cart' : 'Added to cart'
    ).then(function (ok) {
      if (ok) open();
      return ok;
    });
  }

  function changeLine(key, quantity, restoreSelector, row) {
    mutate(
      function () { return post('/cart/change.js', { id: key, quantity: quantity }); },
      restoreSelector,
      quantity === 0 ? 'Item removed' : 'Cart updated',
      quantity === 0 ? collapseRow(row) : null
    );
  }

  function addVariant(id, restoreSelector) {
    mutate(
      function () {
        return post('/cart/add.js', { items: [{ id: id, quantity: 1 }] }).then(function () {
          return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
        });
      },
      restoreSelector,
      'Added to cart'
    );
  }

  /* ---- delegated events -------------------------------------------------- */
  /* Delegated on document because refresh() replaces the drawer's innerHTML,
     which would throw away any handler bound to the elements themselves. */

  document.addEventListener('click', function (e) {
    var el = root();
    if (!el || !e.target.closest) return;

    var closer = e.target.closest('[data-dw-cart-close]');
    if (closer && el.contains(closer)) {
      e.preventDefault();
      close();
      return;
    }

    var qty = e.target.closest('[data-dw-cart-qty]');
    if (qty && el.contains(qty)) {
      e.preventDefault();
      if (qty.getAttribute('aria-disabled') === 'true') return;
      var to = parseInt(qty.getAttribute('data-to'), 10);
      if (isNaN(to) || to < 0) return;
      var row = qty.closest('[data-dw-cart-item]');
      // Show the new number immediately. The re-render is authoritative and
      // will correct it a moment later; waiting for the round trip first is
      // what makes a stepper feel unresponsive.
      if (to > 0 && !reduced()) {
        var valueEl = qty.parentNode && qty.parentNode.querySelector('.dw-cart__qty-value');
        if (valueEl) {
          valueEl.textContent = to;
          valueEl.setAttribute('data-bumped', '');
          setTimeout(function () { valueEl.removeAttribute('data-bumped'); }, 160);
        }
      }
      changeLine(qty.getAttribute('data-key'), to, fingerprint(qty), row);
      return;
    }

    var rm = e.target.closest('[data-dw-cart-remove]');
    if (rm && el.contains(rm)) {
      e.preventDefault();
      if (rm.getAttribute('aria-disabled') === 'true') return;
      changeLine(rm.getAttribute('data-key'), 0, null, rm.closest('[data-dw-cart-item]'));
      return;
    }

    var add = e.target.closest('[data-dw-cart-add]');
    if (add && el.contains(add)) {
      e.preventDefault();
      if (add.getAttribute('aria-disabled') === 'true') return;
      var vid = parseInt(add.getAttribute('data-id'), 10);
      if (isNaN(vid)) return;
      // Only the button that was actually pressed spins. Marking them all
      // would say every upsell is loading when one is.
      add.setAttribute('data-dw-loading', '');
      addVariant(vid, null);
    }
  });

  /*
   * Quick add from anywhere on the page — currently the mega menu cards.
   *
   * Deliberately a separate listener from the drawer's own handlers above:
   * those are all gated on el.contains(...) because they act on rows inside
   * the drawer, whereas these buttons sit outside it and their job is to add,
   * then open it.
   *
   * The dropdown is left alone rather than force-closed. dw-header.js tracks
   * the open panel in its own closure, so clearing the class from here would
   * desync it and the next hover on the same item would refuse to reopen. Its
   * mouseleave already closes the panel as the pointer moves to the drawer.
   */
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var btn = e.target.closest('[data-dw-quick-add]');
    if (!btn) return;
    e.preventDefault();
    if (btn.hasAttribute('data-dw-loading')) return;

    var vid = parseInt(btn.getAttribute('data-id'), 10);
    if (isNaN(vid)) return;

    btn.setAttribute('data-dw-loading', '');
    post('/cart/add.js', { items: [{ id: vid, quantity: 1 }] })
      .then(function () {
        return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        syncCount(cart);
        announce('Added to cart');
        return refresh(null);
      })
      .then(open)
      .catch(function (err) {
        // post() rejects on a non-ok response, so a sold-out variant lands
        // here with Shopify's own description rather than silently doing
        // nothing.
        announce((err && err.message) || 'Could not add that item.');
      })
      .then(function () {
        btn.removeAttribute('data-dw-loading');
      });
  });

  document.addEventListener('keydown', function (e) {
    if (!isOpen()) return;

    if (e.key === 'Escape') {
      close();
      return;
    }
    if (e.key !== 'Tab') return;

    var el = root();
    var panel = panelOf(el);
    if (!panel) return;

    var focusables = panel.querySelectorAll(
      'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    // Containment, not just wrap-around: if a refresh dropped focus to <body>,
    // the next Tab would otherwise walk the page behind the scrim.
    if (!panel.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
      return;
    }
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  /* ---- take over Flex's drawer ------------------------------------------- */

  function adopt() {
    var jc = window.Shopify && window.Shopify.theme && window.Shopify.theme.jsAjaxCart;
    if (!jc) return false;
    if (jc.__dwAdopted) return true;

    jc.__dwAdopted = true;
    jc.showDrawer = function () {
      refresh(null).then(open);
    };
    jc.hideDrawer = close;
    if (typeof jc.hideMiniCart === 'function') jc.hideMiniCart = close;
    return true;
  }

  if (!adopt()) {
    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      if (adopt() || tries > 40) clearInterval(timer);
    }, 100);
  }

  /*
   * The header cart button. Flex routes it through showDrawer, which is now
   * ours, but bind it directly too so the drawer still opens if Flex's init
   * did not run on a given page.
   *
   * Deliberately NO stopPropagation: dw-header.js closes the search dropdown
   * and mega panels from a document-level click listener, and stopping the
   * event here left them stuck open. A flag on the event is enough to stop
   * Flex's own handler double-opening.
   */
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var trigger = e.target.closest('[data-ajax-cart-trigger]');
    if (!trigger) return;
    if (e.target.closest('[data-dw-cart]')) return;
    if (e.__dwCartHandled) return;
    e.__dwCartHandled = true;
    e.preventDefault();
    refresh(null).then(open);
  }, true);

  window.DWCart = { open: open, close: close, refresh: refresh, add: addItems };
})();
